import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';
import { mcpCall, mcpWrite } from '../lib/mcp';
import { useMcpTools } from '../lib/useMcpTools';
import { useToast } from '../components/Toast/ToastProvider';
import './Settings.css';
import './Privacy.css';

/**
 * Partner resale certificates (cabana.partner_resale_certificates), 2026-09-26.
 *
 * Mike: "Partners need a resale certificate in all of the states that we collect tax in and we
 * should ask for one for anyone that wants to be tax exempt ... we need to store the resale certs
 * details for each shipping state that they have." A partner adds one per shipping state in the
 * partner portal. A complete certificate with the resale attestation is ACCEPTED ON SUBMISSION, in
 * good faith, and an accepted, unexpired one makes that state's orders sales-tax exempt.
 *
 * This page shows every certificate and lets Owner and Admin REJECT one that does not hold up, or
 * accept it again. A rejection stops it exempting new orders; it never rewrites an order already
 * placed, and nothing here deletes a certificate: each one is the evidence for the sales it exempted.
 */

type Status = 'accepted' | 'rejected' | 'superseded' | 'expired';
type CertType = 'state_resale' | 'mtc_uniform' | 'sst';

interface CertRow {
  id: number;
  dealer_id: number;
  dealer_name: string;
  state: string;
  certificate_type: CertType;
  certificate_number: string;
  purchaser_name: string;
  purchaser_address: string;
  business_type: string;
  issued_on: string;
  expires_on: string | null;
  signed_by_name: string;
  signed_by_title: string;
  attested_at: string;
  submitted_by_email: string | null;
  submitted_at: string;
  stored_status: Status;
  status: Status;
  reviewed_at: string | null;
  review_note: string | null;
  superseded_by: number | null;
  exempt_orders: number;
}

const STATUS_LABEL: Record<Status, string> = {
  accepted: 'Accepted',
  rejected: 'Rejected',
  superseded: 'Superseded',
  expired: 'Expired',
};
const TYPE_LABEL: Record<CertType, string> = {
  state_resale: 'State resale certificate',
  mtc_uniform: 'MTC Uniform certificate',
  sst: 'SST Certificate of Exemption',
};

const when = (v: string | null) => (v ? new Date(v).toLocaleString() : '—');

function statusTag(s: Status) {
  const mod = s === 'accepted' ? ' records__tag--ok' : s === 'rejected' || s === 'expired' ? ' records__tag--warn' : '';
  return <span className={`records__tag${mod}`}>{STATUS_LABEL[s] ?? s}</span>;
}

const COLUMNS: Column<CertRow>[] = [
  { key: 'submitted_at', header: 'Submitted', sortable: true, render: (r) => when(r.submitted_at) },
  { key: 'dealer_name', header: 'Partner', sortable: true, render: (r) => `${r.dealer_name} (#${r.dealer_id})` },
  { key: 'state', header: 'State', sortable: true },
  { key: 'status', header: 'Status', render: (r) => statusTag(r.status) },
  {
    key: 'certificate_number',
    header: 'Certificate',
    render: (r) => (
      <span className="privacy__result">
        {TYPE_LABEL[r.certificate_type] ?? r.certificate_type}
        <br />
        <code className="privacy__id">{r.certificate_number}</code>
      </span>
    ),
  },
  {
    key: 'purchaser_name',
    header: 'Purchaser',
    render: (r) => (
      <span className="privacy__result">
        {r.purchaser_name}
        <br />
        {r.purchaser_address}
        <br />
        {r.business_type}
      </span>
    ),
  },
  {
    key: 'issued_on',
    header: 'Valid',
    sortable: true,
    render: (r) => `${r.issued_on} to ${r.expires_on ?? 'no expiry'}`,
  },
  {
    key: 'signed_by_name',
    header: 'Signed / attested',
    render: (r) => (
      <span className="privacy__result">
        {r.signed_by_name}, {r.signed_by_title}
        <br />
        Resale attested {when(r.attested_at)}
      </span>
    ),
  },
  { key: 'exempt_orders', header: 'Exempt orders', numeric: true, sortable: true },
  {
    key: 'review_note',
    header: 'Review',
    render: (r) =>
      r.reviewed_at ? (
        <span className="privacy__result">
          {when(r.reviewed_at)}
          {r.review_note ? <><br />{r.review_note}</> : null}
        </span>
      ) : r.superseded_by ? (
        `Replaced by #${r.superseded_by}`
      ) : (
        '—'
      ),
  },
];

const FILTERS: FilterDef<CertRow>[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'all', label: 'All' },
      ...(Object.keys(STATUS_LABEL) as Status[]).map((s) => ({ value: s, label: STATUS_LABEL[s] })),
    ],
    match: (r, v) => r.status === v,
  },
];

function Counts() {
  const q = useQuery({
    queryKey: ['mcp', 'listResaleCertificates', 'counts'],
    queryFn: async () =>
      (await mcpCall<{ statusCounts: Record<Status, number> }>('listResaleCertificates', { limit: 1 })).statusCounts,
  });
  if (!q.data) return null;
  return (
    <dl className="privacy__counts" aria-label="Certificates by status">
      {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
        <div className="privacy__count" key={s}>
          <dt>{STATUS_LABEL[s]}</dt>
          <dd>{q.data[s] ?? 0}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Reject or re-accept the selected certificate. Shows only for a role the MCP grants the action to. */
function ReviewCard({ row, onDone }: { row: CertRow | null; onDone: () => void }) {
  const { can } = useMcpTools();
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!can('reviewResaleCertificate') || !row) return null;

  const decision: 'accept' | 'reject' = row.status === 'rejected' ? 'accept' : 'reject';
  const needsNote = decision === 'reject';
  const valid = !needsNote || note.trim() !== '';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !valid || !row) return;
    setBusy(true);
    setError(null);
    try {
      const r = await mcpWrite<{ status: Status; unpaidExemptOrders: number }>('reviewResaleCertificate', {
        id: row.id,
        decision,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      const tail =
        decision === 'reject' && r.unpaidExemptOrders > 0
          ? ` ${r.unpaidExemptOrders} unpaid order${r.unpaidExemptOrders === 1 ? '' : 's'} placed under it will have to be placed again, taxed.`
          : '';
      notify({ variant: 'success', message: `Certificate #${row.id} (${row.dealer_name}, ${row.state}) is now ${STATUS_LABEL[r.status].toLowerCase()}.${tail}` });
      setNote('');
      await queryClient.invalidateQueries({ queryKey: ['mcp', 'listResaleCertificates'] });
      onDone();
    } catch (err) {
      setError((err as Error).message);
      notify({ variant: 'error', message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings__list">
      <form className="settings__row" onSubmit={submit} aria-labelledby="review-title">
        <div className="settings__meta">
          <p className="settings__key" id="review-title">
            {decision === 'reject' ? 'Reject' : 'Accept again'}: certificate #{row.id}, {row.dealer_name}, {row.state}
          </p>
          <p className="settings__desc">
            {decision === 'reject'
              ? 'Rejecting stops this certificate exempting new orders shipped to this state; they are taxed where Cabana collects. Orders already paid are not changed. The partner sees your note as the reason, so write it for them.'
              : 'Accepting makes this certificate exempt this partner’s orders to this state again. Refused if the partner has since added another accepted certificate for the state.'}
          </p>
          {error && <p className="privacy__error" role="alert">{error}</p>}
        </div>
        <div className="account__form">
          <label className="privacy__field">
            {needsNote ? 'Reason (shown to the partner)' : 'Note (optional)'}
            <textarea
              className="settings__input"
              rows={3}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
          </label>
          <button type="submit" className="settings__save" disabled={busy || !valid}>
            {busy ? 'Saving…' : decision === 'reject' ? 'Reject certificate' : 'Accept certificate'}
          </button>
          <button type="button" className="settings__save" onClick={onDone} disabled={busy}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export function ResaleCertificates() {
  const { can } = useMcpTools();
  const [selected, setSelected] = useState<CertRow | null>(null);
  const canReview = can('reviewResaleCertificate');
  return (
    <>
      <ReviewCard row={selected} onDone={() => setSelected(null)} />
      <Counts />
      <RecordsTable<CertRow>
        title="Resale certificates"
        intro="Partners add a resale certificate for each state they want tax exemption in. A complete certificate with the resale attestation is accepted when submitted, in good faith, and an accepted, unexpired one makes that state's orders tax exempt. Reject one that does not hold up. Certificates are never deleted: each is the evidence for the sales it exempted."
        tool="listResaleCertificates"
        args={{ limit: 500 }}
        select={(r) => (r as { certificates: CertRow[] }).certificates ?? []}
        columns={COLUMNS}
        rowKey={(r) => r.id}
        emptyMessage="No resale certificates yet."
        refreshable
        searchable={{
          placeholder: 'Search partner, state or certificate number…',
          match: (r, q) =>
            [r.dealer_name, r.state, r.certificate_number, r.purchaser_name].some((f) => f != null && f.toLowerCase().includes(q)),
        }}
        filters={FILTERS}
        defaultSort={{ key: 'submitted_at', dir: 'desc' }}
        actions={
          canReview
            ? {
                header: '',
                render: (r) =>
                  r.status === 'accepted' || r.status === 'rejected' ? (
                    <button type="button" className="settings__save" onClick={() => setSelected(r)}>
                      {r.status === 'accepted' ? 'Reject…' : 'Accept…'}
                    </button>
                  ) : null,
              }
            : undefined
        }
      />
    </>
  );
}
